/**
 * Currency-aware icon picker.
 *
 * Lucide ships dedicated glyphs for the major reserve currencies; everything
 * else falls back to the generic `CircleDollarSign`. Companion to
 * `@/lib/currency#formatSalary`.
 *
 * Part of P6-2. Keep this dumb — no formatting, no number rendering, just
 * picks the right SVG.
 */

import {
  IndianRupee,
  DollarSign,
  Euro,
  PoundSterling,
  JapaneseYen,
  CircleDollarSign,
  type LucideIcon,
} from 'lucide-react';

export interface CurrencyIconProps {
  /** ISO 4217 currency code. Null / undefined / unknown → generic icon. */
  currency: string | null | undefined;
  className?: string;
  size?: number;
}

const CURRENCY_TO_ICON: Record<string, LucideIcon> = {
  INR: IndianRupee,
  USD: DollarSign,
  EUR: Euro,
  GBP: PoundSterling,
  JPY: JapaneseYen,
};

export function resolveCurrencyIcon(currency: string | null | undefined): LucideIcon {
  if (!currency) return CircleDollarSign;
  const upper = currency.toUpperCase();
  return CURRENCY_TO_ICON[upper] ?? CircleDollarSign;
}

export function CurrencyIcon({ currency, className, size }: CurrencyIconProps) {
  const Icon = resolveCurrencyIcon(currency);
  return <Icon className={className} size={size} aria-hidden="true" />;
}
